import React, { FC, useEffect, useState, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import pick from 'lodash/pick'
import LeftArrowIcon from 'transfix-icons/build/LeftArrowIcon'
import { Box, makeStyles } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useTeamMemberContext } from 'apps/fleet-planner/context/TeamMemberContext'
import { useTeamMemberFormContext } from 'apps/fleet-planner/context/TeamMemberFormContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useTeamMembersApi } from 'apps/fleet-planner/hooks/useTeamMembersApi'
import { TeamMemberModalSchema, TeamMemberModalSchemaType } from 'apps/fleet-planner/schema'
import { IMotorCarrierDetail, ITeamMember } from 'apps/fleet-planner/types'
import { Button, ButtonGroup, IconButton, ToastState, FormModal, IModalAction } from 'transfix-ui'
import { RemoveTeamMemberModal } from './manage-modal/RemoveTeamMemberModal'
import { TeamMemberDetailForm } from './manage-modal/TeamMemberDetailForm'

export interface IManageTeamMemberModalProps {
  onClose: () => void
  onSubmit: (toastState: Omit<ToastState, 'toastOpen'>, updatedTeamMember?: ITeamMember) => void
}

const useFormTitleStyles = makeStyles(theme => ({
  root: {
    [theme.breakpoints.down('md')]: {
      marginLeft: `-${theme.spacing(3)}`,
    },
  },
}))

export const TeamMemberManageModal: FC<IManageTeamMemberModalProps> = ({ onClose, onSubmit }) => {
  const { trackEvent } = useFMSAnalytics()
  const { isMobile } = useBreakpointContext()
  const { createTeamMember, updateTeamMember } = useTeamMembersApi()
  const {
    currentTeamMember,
    modalMode,
    selectedTeamMember,
    setModalMode,
    setSelectedTeamMember,
  } = useTeamMemberContext()
  const { control, errors, handleSubmit, reset, setError } = useTeamMemberFormContext()
  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState<boolean>(false)
  const [initialCarriers, setInitialCarriers] = useState<IMotorCarrierDetail[]>([])

  const formMotorCarrierDetails = useWatch<IMotorCarrierDetail[]>({
    name: 'motorCarrierDetails',
    defaultValue: [],
    control,
  })

  const selectedCarrierIds = useMemo(() => {
    const selectedCarriers = (formMotorCarrierDetails || []).filter(mcd => mcd.checked)
    return selectedCarriers.map(mcd => mcd.motorCarrierId)
  }, [formMotorCarrierDetails])

  const removedCarriersItems = useMemo(() => {
    return initialCarriers.filter(mcd => !selectedCarrierIds.includes(mcd.motorCarrierId))
  }, [initialCarriers, selectedCarrierIds])

  const getDefaultFormStateForNewUser = () => {
    const userMotorCarrierDetails = (currentTeamMember?.motorCarrierDetails || []).map(
      currentCarrier => ({
        ...currentCarrier,
        checked: false,
        role: undefined,
        userType: undefined,
      })
    )
    return {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      cellPhone: '',
      motorCarrierDetails: userMotorCarrierDetails,
    }
  }

  const getDefaultFormStateForExistingUser = () => {
    const userMotorCarrierDetails = (currentTeamMember?.motorCarrierDetails || []).map(
      currentCarrier => {
        const selectedCarrierDetail = (selectedTeamMember?.motorCarrierDetails || []).find(
          smcd => smcd.motorCarrierId === currentCarrier.motorCarrierId
        )
        const checked = (selectedTeamMember?.motorCarrierDetails || []).some(
          selectedCarriers => selectedCarriers.motorCarrierId === currentCarrier.motorCarrierId
        )
        return {
          ...currentCarrier,
          checked,
          role: selectedCarrierDetail?.role,
          userType: selectedCarrierDetail?.userType,
        }
      }
    )
    return {
      firstName: selectedTeamMember?.firstName || '',
      lastName: selectedTeamMember?.lastName || '',
      email: selectedTeamMember?.email || '',
      phone: selectedTeamMember?.phone || '',
      cellPhone: selectedTeamMember?.cellPhone || '',
      motorCarrierDetails: userMotorCarrierDetails,
    }
  }

  // Reset form state when changing modal mode or selected carrier
  useEffect(() => {
    const defaultFormState =
      modalMode === 'add_new'
        ? getDefaultFormStateForNewUser()
        : getDefaultFormStateForExistingUser()
    reset(TeamMemberModalSchema.cast(defaultFormState))
    setInitialCarriers([...defaultFormState.motorCarrierDetails.filter(mcd => mcd.checked)])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeamMember, modalMode])

  const cancelModalHandler = () => {
    setIsRemoveModalVisible(false)
    onClose()
  }

  const createHandler = async (payload: RecursivePartial<ITeamMember>) => {
    // submit create request
    let createdTeamMember
    let toastMessage
    try {
      createdTeamMember = await createTeamMember(payload)
      const { firstName = '', lastName = '' } = createdTeamMember || {}
      const fullNameString = firstName && lastName ? ` (“${firstName} ${lastName}”)` : ''
      toastMessage = createdTeamMember.isExternal
        ? `Your team member already has a Transfix account${fullNameString}. They’ve been added to your carrier.`
        : 'Team member added.'
    } catch (err) {
      // create error notification message from backend response
      toastMessage = err || 'An unexpected error occurred.'
      if (err.includes('phone') || err.includes('email')) {
        const message =
          'The phone number or email you entered is already associated to a different user. Please ensure your team member is not already added. If the issue persists, email us at '
        const email = 'carriersuccess@transfix.io'
        toastMessage = (
          <span>
            {message} <a href={`mailto:${email}`}>{email}</a>.
          </span>
        )
      }
    }

    // show submission outcome using a toast
    onSubmit(
      {
        toastMessage,
        multiLine: true,
        toastType: createdTeamMember ? 'default' : 'alert',
      },
      createdTeamMember
    )
    if (createdTeamMember) {
      trackEvent('Team Management', 'TEAM_MEMBER_ADD_SUCCESS')
      cancelModalHandler()
    } else {
      trackEvent('Team Management', 'TEAM_MEMBER_ADD_ERROR')
    }
  }

  const updateHandler = async (payload: RecursivePartial<ITeamMember>) => {
    if (!selectedTeamMember?.id) return
    // submit update request
    let updatedTeamMember
    let toastMessage
    try {
      updatedTeamMember = await updateTeamMember(payload, selectedTeamMember.id)
      const { motorCarrierDetails = [] } = updatedTeamMember || {}
      toastMessage = motorCarrierDetails.length ? 'Team Member Updated.' : 'Team Member Removed.'
    } catch (e) {
      // create error notification message from backend response
      if (errors.email && isEqual(e, 'Error: Validation failed: Email has already been taken')) {
        setError('email', { type: 'manual', shouldFocus: true, message: e })
      }
      toastMessage = e || 'An unexpected error occurred.'
    }
    // show submission outcome using a toast
    onSubmit(
      {
        toastType: updatedTeamMember ? 'default' : 'alert',
        toastMessage,
      },
      updatedTeamMember
    )
    if (updatedTeamMember) {
      trackEvent('Team Management', 'TEAM_MEMBER_ADD_SUCCESS')
      cancelModalHandler()
    } else {
      trackEvent('Team Management', 'TEAM_MEMBER_EDIT_ERROR')
    }
  }

  const submitHandler = async (formData: TeamMemberModalSchemaType) => {
    const { motorCarrierDetails = [] } = formData
    // prepare the request payload
    const details = motorCarrierDetails
      .filter(mcd => mcd.checked)
      .map(mcd => pick(mcd, ['motorCarrierId', 'role', 'userType']))
    const payload = {
      ...formData,
      motorCarrierDetails: details,
    }
    if (modalMode === 'add_new') {
      createHandler(payload)
    } else if (modalMode === 'add_existing' || modalMode === 'edit_existing') {
      updateHandler(payload)
    }
  }

  const remoteModalDismissHandler = () => {
    setIsRemoveModalVisible(false)
  }

  const removeModalConfirmHandler = () => {
    handleSubmit(
      submitHandler, // if validation passes
      remoteModalDismissHandler // else if it fails
    )()
  }

  const addModeToggleHandler = () => {
    if (modalMode === 'add_new') {
      setModalMode('add_existing')
      setSelectedTeamMember(undefined)
    } else if (modalMode === 'add_existing') {
      setModalMode('add_new')
    }
  }

  const actionConfirm: IModalAction = {
    label: 'Save',
    type: 'confirm',
    disabled: !isEmpty(errors),
    action: () => {
      if (removedCarriersItems.length) {
        setIsRemoveModalVisible(true)
      } else {
        handleSubmit(submitHandler)()
      }
    },
  }

  if (isMobile) {
    actionConfirm.label = 'Save'
  }

  let modalActions: IModalAction[] = [actionConfirm]

  if (!isMobile) {
    modalActions = [
      {
        label: 'Cancel',
        type: 'dismiss',
        action: cancelModalHandler,
      },
      ...modalActions,
    ]
  }

  const formTitleClasses = useFormTitleStyles()

  const formTitle = (
    <Box display='flex' alignItems='center' className={formTitleClasses.root}>
      <Box paddingX={1}>
        <IconButton
          Icon={LeftArrowIcon}
          color='tertiary'
          onClick={onClose}
          label='Go back one page'
          noPadding
          size='large'
        />
      </Box>
      {modalMode === 'edit_existing' ? 'Edit Team Member' : 'Add Team Member'}
    </Box>
  )

  const showToggleNewOrInvite = modalMode !== 'edit_existing'
  const showModalError =
    !isMobile && !isEmpty(errors) ? 'Please fill out the required fields below.' : undefined

  return (
    <>
      <FormModal
        title={formTitle}
        actions={modalActions}
        open
        onClose={onClose}
        size={6}
        error={showModalError}>
        {showToggleNewOrInvite && (
          <Box mb={4}>
            <ButtonGroup value={modalMode} onChange={addModeToggleHandler} hideLabel={isMobile}>
              <Button size='large' value='add_new'>
                Add a new team member
              </Button>
              <Button size='large' value='add_existing'>
                Invite an existing team member to a carrier
              </Button>
            </ButtonGroup>
          </Box>
        )}

        <Box mb={4}>
          <TeamMemberDetailForm />
        </Box>
      </FormModal>
      {isRemoveModalVisible && (
        <RemoveTeamMemberModal
          removed={removedCarriersItems}
          onClose={remoteModalDismissHandler}
          onSuccess={removeModalConfirmHandler}
        />
      )}
    </>
  )
}
